import type { SignInMethod } from '~/types/User'

interface UserNameParts {
  firstName?: string | null
  lastName?: string | null
  // Null for an integration-provisioned account, which has no mailbox.
  email: string | null
}

/** Two-letter initials from the user's full name, then the email prefix, then
 *  whichever single name part exists, then '?' when there is nothing at all. */
export function getUserInitials(user: UserNameParts): string {
  if (user.firstName && user.lastName) {
    return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
  }

  if (user.email) {
    return user.email.slice(0, 2).toUpperCase()
  }

  return getInitialsFromName(user.firstName || user.lastName) || '?'
}

/** Two-letter initials from a display-name string (first letter of the first
 *  two words). Empty string when there's nothing to derive. */
export function getInitialsFromName(name?: string | null): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean)
  return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase()
}

/** Full name when both parts are present, then the email address, then
 *  whichever single name part exists, then 'Unknown user'. */
export function getUserDisplayName(user: UserNameParts): string {
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`
  }

  if (user.email) {
    return user.email
  }

  return user.firstName || user.lastName || 'Unknown user'
}

const SIGN_IN_METHOD_LABELS: Record<SignInMethod, string> = {
  GoogleOAuth: 'Google',
  password: 'Email + password',
}

/** Human-readable sign-in methods (e.g. "Google · Email + password"), or an
 *  em-dash when the user has none on record. */
export function formatSignInMethods(methods?: SignInMethod[] | null): string {
  if (!methods || methods.length === 0) return '—'
  return methods
    .map((method) => SIGN_IN_METHOD_LABELS[method] ?? method)
    .join(' · ')
}

/** A membership/created date as "Jan 2024", or an em-dash when absent. */
export function formatMemberSince(dateString?: string | null): string {
  if (!dateString) return '—'
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  })
}
